import type { ObstetricalState } from './types'
import type { UseQueryResult } from '@tanstack/react-query'

import { useMutation, type UseMutationResult, useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import http from '../../../../../lib/http'
import { queryClient } from '../../../../../lib/query-client'

import { obstetricalSchema } from './types'

const QUERY_KEY = 'obstetrical-history'

export function useObstetricalHistory (patientId?: string, callback?: () => void): UseQueryResult<{draft: boolean, data: ObstetricalState} | undefined, unknown> {
  return useQuery<
  {draft: boolean, data: ObstetricalState} | undefined,
  unknown,
  {draft: true, data: ObstetricalState} | undefined,
  [ 'obstetrical-history', string | undefined ]
  >({
    queryKey: [ QUERY_KEY, patientId ],
    async queryFn () {
      if (!patientId) {
        return
      }

      const response = await http.getRequest(`/patients/${patientId}/maternity/obstetrical-history`)

      const parsed = obstetricalSchema.nullish().safeParse(response?.data)

      if (!parsed.success) {
        throw new Error(parsed.error.message)
      }

      callback?.()

      return {
        ...response,
        data: parsed.data
      }
    },

    refetchOnWindowFocus: false,
    retry: false
  })
}

export function useObstetricalSubmitMutation (patientId?: string, callback?: () => void): UseMutationResult<unknown, unknown, ObstetricalState, unknown> {
  return useMutation<unknown, unknown, ObstetricalState, unknown>({
    async mutationFn (obstetricalHistory) {
      if (!patientId) {
        return
      }

      const deliveryHistoryData: Partial<ObstetricalState> = {
        id: obstetricalHistory.id,
        para: obstetricalHistory.para,
        nvd: obstetricalHistory.nvd,
        vbac: obstetricalHistory.vbac,
        gravida: obstetricalHistory.gravida,
        cs: obstetricalHistory.cs
      }

      return await http.postRequest(`/patients/${patientId}/maternity/obstetrical-history`, deliveryHistoryData)
    },
    onSuccess () {
      void queryClient.invalidateQueries({ queryKey: [ QUERY_KEY, patientId ?? '' ] })
      void queryClient.invalidateQueries({ queryKey: [ 'drafts', patientId ] })

      toast.success('Obstetrical History Successfully Saved', { position: 'bottom-center' })
      callback?.()
    },
    onError () {
      toast.error('Something went wrong while saving Obstetrical History')
    }
  })
}

export function useObstetricalDeleteDraft (patientId?: string, callback?: () => void): UseMutationResult<unknown, unknown, null, unknown> {
  return useMutation<unknown, unknown, null, unknown>({
    async mutationFn () {
      if (!patientId) {
        return
      }

      return await http.deleteRequest(`/patients/${patientId}/maternity/obstetrical-history/draft`)
    },
    async onSuccess () {
      queryClient.invalidateQueries({ queryKey: [ QUERY_KEY, patientId ?? '' ] })
        .catch((e) => {
          console.error(e)
        })
      await queryClient.invalidateQueries({ queryKey: [ 'drafts', patientId ] })
      callback?.()
    }
  })
}

export function useObstetricalDraftMutation (patientId?: string, callback?: () => void): UseMutationResult<unknown, unknown, Partial<ObstetricalState>, unknown> {
  return useMutation<unknown, unknown, Partial<ObstetricalState>, unknown>({
    async mutationFn (deliveryHistory) {
      if (!patientId) {
        return
      }

      await http.putRequest(`/patients/${patientId}/maternity/obstetrical-history/draft`, deliveryHistory)
    },
    async onSuccess (data) {
      await queryClient.invalidateQueries({ queryKey: [ 'drafts', patientId ] })
      callback?.()
    }
  })
}
